import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { Box, Chip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

export type AssistantChatInputValue = { text: string; skill: string | null }

export type AssistantChatInputComboboxProps = {
  'aria-haspopup'?: 'listbox'
  'aria-expanded'?: boolean
  'aria-autocomplete'?: 'list'
  'aria-controls'?: string
  'aria-activedescendant'?: string
}

export type AssistantChatInputProps = {
  value: AssistantChatInputValue
  onChange: (next: AssistantChatInputValue) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  placeholder: string
  disabled?: boolean
  // ARIA combobox attributes spread onto the textbox so the input + slash menu
  // form a discoverable combobox for screen readers (WCAG 2.1 AA, 1.3.1/4.1.2).
  // Owned by the parent because it's the parent that knows menu open state and
  // active option id.
  comboboxProps?: AssistantChatInputComboboxProps
}

const CHIP_DATA_ATTR = 'data-skill-chip'
const CHIP_TEST_ID = 'assistant-chat-input-chip'
const ROOT_TEST_ID = 'assistant-chat-input'

/**
 * Read the current plain-text content of the editable area, ignoring the chip
 * element. `<br>` tags become newlines (browsers insert them on Shift+Enter).
 */
const readPlainText = (root: HTMLElement): string => {
  let text = ''
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? ''
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement
      if (el.hasAttribute(CHIP_DATA_ATTR)) return
      if (el.tagName === 'BR') {
        text += '\n'
      } else {
        text += el.textContent ?? ''
      }
    }
  })
  return text
}

const findChipNode = (root: HTMLElement): HTMLElement | null =>
  root.querySelector(`[${CHIP_DATA_ATTR}]`)

/**
 * Whether the current selection is collapsed and positioned immediately after
 * the chip with no other content between the chip and the caret.
 */
const isCaretImmediatelyAfterChip = (root: HTMLElement): boolean => {
  const selection = window.getSelection()
  if (!selection || !selection.isCollapsed) return false
  const { anchorNode, anchorOffset } = selection
  if (!anchorNode || !root.contains(anchorNode)) return false

  const chip = findChipNode(root)
  if (!chip) return false

  if (anchorNode === root) {
    const chipIndex = Array.from(root.childNodes).indexOf(chip as ChildNode)
    return anchorOffset === chipIndex + 1
  }

  if (anchorNode.nodeType === Node.TEXT_NODE && anchorOffset === 0) {
    return anchorNode.previousSibling === chip
  }

  return false
}

/**
 * Move the caret to the end of the editable area.
 */
const placeCaretAtEnd = (root: HTMLElement) => {
  const selection = window.getSelection()
  if (!selection) return
  const range = document.createRange()
  range.selectNodeContents(root)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
}

/**
 * Replace the editable area's text-node children with `text`, leaving the
 * chip (if any) intact at the start.
 */
const writeText = (root: HTMLElement, text: string) => {
  Array.from(root.childNodes).forEach((node) => {
    const el = node as HTMLElement
    if (node.nodeType === Node.ELEMENT_NODE && el.hasAttribute(CHIP_DATA_ATTR)) return
    root.removeChild(node)
  })
  if (text) root.appendChild(document.createTextNode(text))
}

/**
 * Chip-aware multiline message input for the assistant chat.
 *
 * Renders a `contentEditable` `<div>` that holds plain text plus, optionally,
 * a single non-editable MUI `<Chip>` representing a picked skill. Chip
 * presence is the source of truth for `value.skill`.
 *
 * Behavior:
 * - Typing emits `onChange({ text, skill })` with the parsed text content.
 * - Backspace immediately after the chip (no text between caret and chip)
 *   clears the chip via `onChange({ text, skill: null })`.
 * - Pasting plain text inserts literal text only — never auto-creates a chip
 *   (req 6).
 * - Disabled state makes the editable area read-only without removing
 *   content.
 */
const AssistantChatInput = forwardRef<HTMLDivElement, AssistantChatInputProps>((props, ref) => {
  const { value, onChange, onKeyDown, placeholder, disabled = false, comboboxProps } = props
  const rootRef = useRef<HTMLDivElement | null>(null)
  const valueRef = useRef(value)
  valueRef.current = value

  useImperativeHandle(ref, () => rootRef.current as HTMLDivElement)

  // Sync the editable DOM's text content from the controlled value when they
  // diverge (e.g. parent clears on send). Skipped when DOM already matches,
  // so a user mid-typing keeps their caret position.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (readPlainText(root) !== value.text) {
      writeText(root, value.text)
    }
  }, [value.text])

  // Initial mount: write the starting text into the DOM. The chip itself is
  // rendered by React as a JSX child, so it appears before the text node.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (valueRef.current.text && readPlainText(root) === '') {
      root.appendChild(document.createTextNode(valueRef.current.text))
    }
  }, [])

  // When a skill chip first appears (e.g. picked from the slash menu), park
  // the caret at the end of the editable area so the user can immediately
  // type parameters. Without this, focus() on the contentEditable lands the
  // caret at offset 0 — before the chip — which feels broken.
  useEffect(() => {
    if (!value.skill) return
    const root = rootRef.current
    if (!root) return
    placeCaretAtEnd(root)
  }, [value.skill])

  const emitFromDom = useCallback(() => {
    const root = rootRef.current
    if (!root) return
    const domHasChip = !!findChipNode(root)
    const text = readPlainText(root)
    const { current } = valueRef
    const nextSkill = domHasChip ? current.skill : null
    if (text === current.text && nextSkill === current.skill) return
    onChange({ text, skill: nextSkill })
  }, [onChange])

  const handleInput = useCallback(() => {
    emitFromDom()
  }, [emitFromDom])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        e.key === 'Backspace' &&
        valueRef.current.skill !== null &&
        rootRef.current &&
        isCaretImmediatelyAfterChip(rootRef.current)
      ) {
        e.preventDefault()
        onChange({ text: valueRef.current.text, skill: null })
        return
      }
      if (onKeyDown) onKeyDown(e)
    },
    [onChange, onKeyDown]
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      // Force plain-text paste so a `/` in pasted content stays literal and
      // never auto-creates a chip (req 6).
      e.preventDefault()
      const text = e.clipboardData.getData('text/plain')
      if (!text) return
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        rootRef.current?.appendChild(document.createTextNode(text))
      } else {
        const range = selection.getRangeAt(0)
        range.deleteContents()
        const node = document.createTextNode(text)
        range.insertNode(node)
        range.setStartAfter(node)
        range.setEndAfter(node)
        selection.removeAllRanges()
        selection.addRange(range)
      }
      emitFromDom()
    },
    [emitFromDom]
  )

  const handleChipMouseDown = useCallback((e: React.MouseEvent) => {
    // Clicking the chip should not place the caret inside it. Land the
    // caret at the end of the editable area instead.
    e.preventDefault()
    const root = rootRef.current
    if (!root) return
    root.focus()
    placeCaretAtEnd(root)
  }, [])

  const handleChipDelete = useCallback(() => {
    // Keyboard-only users cannot reliably position the caret immediately
    // after the chip to use Backspace; the visible × button gives them a
    // direct affordance. Returns focus to the editable area with the caret
    // at the end so typing can resume after deletion — focus() alone leaves
    // the caret position browser-dependent.
    onChange({ text: valueRef.current.text, skill: null })
    requestAnimationFrame(() => {
      const root = rootRef.current
      if (!root) return
      root.focus()
      placeCaretAtEnd(root)
    })
  }, [onChange])

  const showPlaceholder = !value.skill && value.text.length === 0

  return (
    <Box
      sx={{
        flex: 1,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        minHeight: 24
      }}
    >
      <Box
        ref={rootRef}
        role="textbox"
        aria-label="Message"
        aria-multiline="true"
        aria-readonly={disabled ? true : undefined}
        aria-disabled={disabled ? true : undefined}
        {...comboboxProps}
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-testid={ROOT_TEST_ID}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        sx={{
          flex: 1,
          minWidth: 40,
          outline: 'none',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: '0.875rem',
          lineHeight: '1.5',
          cursor: disabled ? 'default' : 'text',
          maxHeight: '6em',
          overflowY: 'auto'
        }}
      >
        {value.skill && (
          <span
            contentEditable={false}
            {...{ [CHIP_DATA_ATTR]: '' }}
            data-testid={CHIP_TEST_ID}
            role="presentation"
            style={{
              display: 'inline-flex',
              userSelect: 'none',
              marginRight: 4,
              verticalAlign: 'middle'
            }}
          >
            <Chip
              label={value.skill}
              size="small"
              onMouseDown={handleChipMouseDown}
              onDelete={handleChipDelete}
              deleteIcon={<CloseIcon aria-label={`Remove skill ${value.skill}`} />}
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600
              }}
            />
          </span>
        )}
      </Box>
      {showPlaceholder && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'text.disabled',
            pointerEvents: 'none',
            fontSize: '0.875rem'
          }}
        >
          {placeholder}
        </Box>
      )}
    </Box>
  )
})

AssistantChatInput.displayName = 'AssistantChatInput'

export default AssistantChatInput
